import * as THREE from "three";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import type { NoteCategory } from "../types.ts";
import { snapToVertex } from "./Snap.ts";

export interface NoteResult {
  category: NoteCategory;
  point: THREE.Vector3;
  text: string;
}

export class NoteTool {
  private active = false;
  private category: NoteCategory | null = null;
  private raycaster = new THREE.Raycaster();
  private meshes: THREE.Mesh[] = [];
  private previewDot: THREE.Points | null = null;
  private inputPopup: CSS2DObject | null = null;
  private scene: THREE.Scene;

  onComplete: ((r: NoteResult) => void) | null = null;
  onCancel: (() => void) | null = null;
  onCursorChange: ((active: boolean) => void) | null = null;

  constructor(
    private canvas: HTMLCanvasElement,
    private camera: THREE.Camera,
    scene: THREE.Scene,
  ) {
    this.scene = scene;
    this.raycaster.firstHitOnly = true;
    this._bindEvents();
  }

  activate(category: NoteCategory, meshes: THREE.Mesh[]): void {
    this.category = category;
    this.meshes = meshes;
    this.active = true;
    this.canvas.style.cursor = "crosshair";
    this.onCursorChange?.(true);
  }

  deactivate(): void {
    this.active = false;
    this.category = null;
    this.canvas.style.cursor = "";
    this._clearPreview();
    this._clearPopup();
    this.onCursorChange?.(false);
  }

  dispose(): void {
    this._unbindEvents();
    this._clearPreview();
    this._clearPopup();
  }

  private _castRay(event: MouseEvent): THREE.Vector3 | null {
    const rect = this.canvas.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(mouse, this.camera);
    const hits = this.raycaster.intersectObjects(this.meshes, true);
    if (!hits.length) return null;
    return snapToVertex(hits[0], this.camera, this.canvas);
  }

  private _onMouseMove = (e: MouseEvent): void => {
    if (!this.active || !this.category || this.inputPopup) return;
    const pt = this._castRay(e);
    this._clearPreview();
    if (pt) {
      const geo = new THREE.BufferGeometry().setFromPoints([pt]);
      const mat = new THREE.PointsMaterial({ color: 0x4a9eff, size: 0.06, depthTest: false, sizeAttenuation: true });
      this.previewDot = new THREE.Points(geo, mat);
      this.previewDot.renderOrder = 999;
      this.scene.add(this.previewDot);
    }
  };

  private _onClick = (e: MouseEvent): void => {
    if (!this.active || !this.category || e.button !== 0) return;
    // If popup is open, ignore clicks on canvas (let popup handle it)
    if (this.inputPopup) return;

    const pt = this._castRay(e);
    if (!pt) return;

    this._clearPreview();
    this._showInputPopup(pt);
  };

  private _showInputPopup(point: THREE.Vector3): void {
    const category = this.category!;
    const container = document.createElement("div");
    container.className = "note-input-popup";
    container.innerHTML = `
      <div class="note-input-header">${_categoryLabel(category)}</div>
      <textarea class="note-input-text" rows="2" placeholder="Enter note text..."></textarea>
      <div class="note-input-actions">
        <button class="note-input-cancel">Cancel</button>
        <button class="note-input-save">Save</button>
      </div>
    `;
    // Stop click events from propagating to the canvas
    container.addEventListener("pointerdown", (e) => e.stopPropagation());
    container.addEventListener("click", (e) => e.stopPropagation());

    const textarea = container.querySelector<HTMLTextAreaElement>(".note-input-text")!;
    const saveBtn = container.querySelector<HTMLButtonElement>(".note-input-save")!;
    const cancelBtn = container.querySelector<HTMLButtonElement>(".note-input-cancel")!;

    const save = () => {
      const text = textarea.value.trim();
      if (!text) return;
      this._clearPopup();
      const result: NoteResult = { category, point: point.clone(), text };
      this.deactivate();
      this.onComplete?.(result);
    };

    const cancel = () => {
      this._clearPopup();
      // Stay in placement mode so user can pick another point
    };

    saveBtn.addEventListener("click", save);
    cancelBtn.addEventListener("click", cancel);
    textarea.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(); }
      if (e.key === "Escape") { cancel(); }
    });

    const obj = new CSS2DObject(container);
    obj.position.copy(point);
    this.scene.add(obj);
    this.inputPopup = obj;

    // Focus after a frame so CSS2D has positioned it
    requestAnimationFrame(() => textarea.focus());
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (!this.active) return;
    if (e.key === "Escape" && !this.inputPopup) {
      this.deactivate();
      this.onCancel?.();
    }
  };

  private _clearPreview(): void {
    if (this.previewDot) {
      this.previewDot.geometry.dispose();
      (this.previewDot.material as THREE.Material).dispose();
      this.scene.remove(this.previewDot);
      this.previewDot = null;
    }
  }

  private _clearPopup(): void {
    if (this.inputPopup) {
      this.inputPopup.element.remove();
      this.scene.remove(this.inputPopup);
      this.inputPopup = null;
    }
  }

  private _bindEvents(): void {
    this.canvas.addEventListener("mousemove", this._onMouseMove);
    this.canvas.addEventListener("click", this._onClick);
    window.addEventListener("keydown", this._onKeyDown);
  }

  private _unbindEvents(): void {
    this.canvas.removeEventListener("mousemove", this._onMouseMove);
    this.canvas.removeEventListener("click", this._onClick);
    window.removeEventListener("keydown", this._onKeyDown);
  }
}

function _categoryLabel(cat: NoteCategory): string {
  return { room: "Room", note: "Note", damage: "Damage" }[cat];
}
